import { useState, useEffect, useRef } from "react";
import {
    ChevronDownIcon,
    ChevronUpIcon,
    MessageSquareTextIcon,
    PlayCircleIcon,
    FileTextIcon,
    QuoteIcon,
    SparklesIcon,
    StarIcon,
} from "lucide-react";
import type { BeFeedbackItem, KnowledgeManifest, RubricCriterion } from "@type/training";
import { cn } from "@util/tailwind";

interface CoachingMomentCardProps {
    item: BeFeedbackItem;
    rubricCriterion?: RubricCriterion;
    /** Scoring-time knowledge snapshot, used to resolve `item.source_document_id`
     *  into a document name for the citation chip. The BE has already validated
     *  the id against this same manifest, so a lookup miss means a legacy result
     *  scored before Phase 3 — render nothing rather than a bare id. */
    knowledgeManifest?: KnowledgeManifest | null;
    onOpenSourceDoc?: (documentId: string) => void;
    onPlayFromTimestamp?: (timestamp: string) => void;
    quoteLabel?: string;
    showContentSummary?: boolean;
    // Chat/TXT session: the timestamp action jumps the transcript to this chat
    // message time instead of seeking an audio player, and is worded accordingly.
    isChat?: boolean;
    // When this changes to a non-null value, the card auto-expands. The parent
    // passes a new value (an incrementing signal) to the card matching a
    // clicked RecordingPlayer timeline dot, so re-clicking the same dot
    // re-opens it even after a manual collapse. `null` leaves the card alone.
    expandKey?: number | null;
    // Seeds the card open on mount (the parent uses this for the first item in a
    // list). Unlike `expandKey` it only sets the INITIAL state — it never forces
    // the card back open after a manual collapse, and it doesn't scroll.
    defaultExpanded?: boolean;
}

// Chat feedback timestamps can arrive as "9:04", "09:04:00" or a full datetime;
// show just the "H:MM" clock in the transcript-jump label.
function chatClockLabel(v: string): string {
    const m = v.match(/(\d{1,2}):(\d{2})/);
    return m ? `${Number(m[1])}:${m[2]}` : v;
}

const DOT_COLORS: Record<BeFeedbackItem["category"], string> = {
    Brilliant: "bg-purple-600",
    Excellent: "bg-green-600",
    Good: "bg-emerald-600",
    Inaccuracy: "bg-amber-500",
    Mistake: "bg-red-600",
};

const ACCENT_COLORS: Record<BeFeedbackItem["category"], string> = {
    Brilliant: "bg-purple-500",
    Excellent: "bg-green-500",
    Good: "bg-emerald-500",
    Inaccuracy: "bg-amber-500",
    Mistake: "bg-red-500",
};

const CAPTION_CLASS = "text-gray-500";
const CAPTION_ICON_CLASS = "text-gray-400";

function deriveFirstSentence(content: string): string {
    if (!content) return "";
    const match = content.match(/^[^.!?\n]+[.!?]?/);
    return match ? match[0].trim() : content.slice(0, 120).trim();
}

// Scroll a just-expanded card into view within its NEAREST scrollable ancestor
// only (the right panel's feedback/insights scroll container) — never the page
// — so the left panel (audio recording + transcript) stays static.
function scrollCardIntoView(el: HTMLElement): void {
    let parent: HTMLElement | null = el.parentElement;
    while (parent) {
        const overflowY = getComputedStyle(parent).overflowY;
        if (
            (overflowY === "auto" || overflowY === "scroll") &&
            parent.scrollHeight > parent.clientHeight
        ) {
            break;
        }
        parent = parent.parentElement;
    }
    if (!parent) return;
    const parentRect = parent.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    // Only move if the card isn't already fully in view; align its top a touch
    // below the container top so the heading + expanded body are visible.
    if (elRect.top < parentRect.top || elRect.bottom > parentRect.bottom) {
        const delta = elRect.top - parentRect.top - 12;
        parent.scrollTo({ top: parent.scrollTop + delta, behavior: "smooth" });
    }
}

export function CoachingMomentCard({
    item,
    rubricCriterion,
    knowledgeManifest,
    onOpenSourceDoc,
    onPlayFromTimestamp,
    quoteLabel = "Quoted from your answer",
    showContentSummary = false,
    isChat = false,
    expandKey = null,
    defaultExpanded = false,
}: CoachingMomentCardProps) {
    const [open, setOpen] = useState(defaultExpanded);
    const cardRef = useRef<HTMLDivElement>(null);

    // Auto-expand when the parent targets this card (e.g. its timeline dot was
    // clicked). Only opens — never force-collapses, so manual toggles stick.
    // After opening, scroll the card into view within the right panel so cards
    // near the bottom aren't expanded off-screen.
    useEffect(() => {
        if (expandKey === null) return;
        setOpen(true);
        // Defer one frame so the expanded body is in the DOM before measuring.
        const raf = requestAnimationFrame(() => {
            if (cardRef.current) scrollCardIntoView(cardRef.current);
        });
        return () => cancelAnimationFrame(raf);
    }, [expandKey]);
    const ts = item.timestamp?.trim();
    const contentSummary = showContentSummary ? deriveFirstSentence(item.content) : null;
    const isNegative = item.category === "Inaccuracy" || item.category === "Mistake";
    const greatLooksLike =
        isNegative && rubricCriterion?.correct_answer ? rubricCriterion.correct_answer : null;

    const explanation = item.content;

    // Resolve the cited document from the manifest. Only docs the evaluator was
    // actually given can be cited, so an unresolvable id is dropped silently —
    // showing "Source: unknown" would undermine the point of the citation.
    const sourceDoc = item.source_document_id
        ? (knowledgeManifest?.documents ?? []).find(
              (d) => d.document_id === item.source_document_id,
          ) ?? null
        : null;

    return (
        <div ref={cardRef} className="border-b border-gray-100 last:border-0">
            {/* Header row */}
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={cn(
                    "w-full text-left py-4 px-4 flex items-start gap-3 cursor-pointer hover:bg-gray-50 transition-colors",
                    open && "bg-gray-50",
                )}
                aria-expanded={open}
            >
                <span
                    className={cn(
                        "w-2 h-2 rounded-full mt-1.5 flex-shrink-0",
                        DOT_COLORS[item.category],
                    )}
                />
                <span className="flex-grow min-w-0">
                    <span className="block text-sm font-medium text-gray-900">
                        {item.title}
                    </span>
                    {contentSummary && contentSummary !== item.title && (
                        <span className="block text-xs text-gray-500 mt-0.5 leading-relaxed">
                            {contentSummary}
                        </span>
                    )}
                </span>
                {/* Score deduction tag — only on negative moments that carry a
                    point impact. Absent until the BE emits per-moment points. */}
                {isNegative &&
                    typeof item.points_lost === "number" &&
                    item.points_lost > 0 && (
                        <span
                            title={`This moment lowered the overall score by ${item.points_lost}.`}
                            className="inline-flex items-center rounded-md px-1.5 py-0.5 mt-0.5 text-xs font-semibold border bg-rose-50 text-rose-700 border-rose-200 tabular-nums whitespace-nowrap flex-shrink-0"
                        >
                            −{item.points_lost}
                        </span>
                    )}
                <span className="flex-shrink-0">
                    {open ? (
                        <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                    )}
                </span>
            </button>

            {/* Expanded body */}
            {open && (item.quote || explanation || (onPlayFromTimestamp && ts)) && (
                <div className="px-4 pb-5 mt-4">
                    <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
                        <div className="flex">
                            <div
                                className={cn("w-1 flex-shrink-0", ACCENT_COLORS[item.category])}
                                aria-hidden
                            />
                            <div className="flex-1 p-4 space-y-4">
                                {/* Quote */}
                                {item.quote && (
                                    <div>
                                        <div
                                            className={cn(
                                                "flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-wider font-semibold",
                                                CAPTION_CLASS,
                                            )}
                                        >
                                            <QuoteIcon
                                                className={cn("w-3 h-3", CAPTION_ICON_CLASS)}
                                            />
                                            {quoteLabel}
                                        </div>
                                        <div className="rounded-md bg-neutral-50 border border-neutral-200 px-4 py-3">
                                            <p className="text-sm italic text-gray-700 leading-relaxed">
                                                "{item.quote}"
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* What great looks like — Inaccuracy/Mistake only */}
                                {greatLooksLike && (
                                    <div>
                                        <div
                                            className={cn(
                                                "flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-wider font-semibold",
                                                CAPTION_CLASS,
                                            )}
                                        >
                                            <StarIcon
                                                className={cn("w-3 h-3", CAPTION_ICON_CLASS)}
                                            />
                                            What great looks like
                                        </div>
                                        <ul className="space-y-2">
                                            {greatLooksLike.split("\n").filter(Boolean).map((line, li) => (
                                                <li key={li} className="flex items-start gap-2 text-sm text-gray-800">
                                                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                                                    <span className="leading-relaxed">{line}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Explanation */}
                                {explanation && (
                                    <div>
                                        <div
                                            className={cn(
                                                "flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-wider font-semibold",
                                                CAPTION_CLASS,
                                            )}
                                        >
                                            <SparklesIcon
                                                className={cn("w-3 h-3", CAPTION_ICON_CLASS)}
                                            />
                                            Coach&apos;s note
                                        </div>
                                        <p className="text-sm text-gray-800 leading-relaxed">
                                            {explanation}
                                        </p>
                                        {/* Citation chip — sits with the coach's note because
                                            it attributes THAT claim, not the whole card. */}
                                        {sourceDoc && (
                                            <button
                                                type="button"
                                                disabled={!onOpenSourceDoc}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onOpenSourceDoc?.(sourceDoc.document_id);
                                                }}
                                                title={
                                                    sourceDoc.truncated
                                                        ? `Based on ${sourceDoc.name} (only part of this document fit the evaluation context)`
                                                        : `Based on ${sourceDoc.name}`
                                                }
                                                className={cn(
                                                    "mt-2.5 inline-flex items-center gap-1.5 max-w-full rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors",
                                                    onOpenSourceDoc &&
                                                        "hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 cursor-pointer",
                                                )}
                                            >
                                                <FileTextIcon className="w-3 h-3 flex-shrink-0" />
                                                <span className="truncate">{sourceDoc.name}</span>
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Timestamp play */}
                                {onPlayFromTimestamp && ts && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onPlayFromTimestamp(ts);
                                        }}
                                        className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                                    >
                                        {isChat ? (
                                            <MessageSquareTextIcon className="w-3.5 h-3.5" />
                                        ) : (
                                            <PlayCircleIcon className="w-3.5 h-3.5" />
                                        )}
                                        {isChat ? `Go to ${chatClockLabel(ts)} in transcript` : `Play from ${ts}`}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
